import * as React from 'react';
import { useOrganisation } from '../../Hooks/useOrganisation';
import { useCookieContext, useUserInfo } from '../../Hooks/useUserInfo';

export default function useRequiredLivePageData(token: string | undefined, cognitoOrg?: string) {
  const isTokenReady = !!token;
  const {
    org: orgOverride,
    removeToken,
    setOrgOverride,
  } = useCookieContext();
  const userInfo = useUserInfo(token, { enabled: isTokenReady });
  const organisationInfo = useOrganisation({ enabled: isTokenReady });

  React.useEffect(() => {
    if (userInfo.isSuccess && !userInfo.isError && userInfo.data === undefined) {
      removeToken('user_not_found');
    }
  }, [removeToken, userInfo.data, userInfo.isError, userInfo.isSuccess]);

  React.useEffect(() => {
    const fetchedOrgId = userInfo.data?.getUserInfo.organisationId;
    if (!cognitoOrg || !fetchedOrgId) {
      return;
    }

    if (fetchedOrgId !== cognitoOrg && orgOverride !== cognitoOrg) {
      setOrgOverride(cognitoOrg);
      Promise.all([
        userInfo.refetch(),
        organisationInfo.refetch(),
      ]).catch(() => undefined);
    }
  }, [
    cognitoOrg,
    orgOverride,
    organisationInfo,
    setOrgOverride,
    userInfo,
  ]);

  return {
    isReady: !!userInfo.data && !!organisationInfo.data,
    isLoading: isTokenReady && (
      userInfo.isLoading
      || userInfo.isFetching
      || organisationInfo.isLoading
      || organisationInfo.isFetching
      || (!userInfo.isError && !userInfo.data)
      || (!organisationInfo.isError && !organisationInfo.data)
    ),
    error: userInfo.error || organisationInfo.error,
    refetch() {
      Promise.all([
        userInfo.refetch(),
        organisationInfo.refetch(),
      ]).catch(() => undefined);
    },
  };
}
